import clsx from "clsx";
import type React from "react";
import "./Typography.css";

const styles: Record<string, string> = {
  root: "calmlens-typography-root",
  display: "calmlens-typography-display",
  heading1: "calmlens-typography-heading1",
  heading2: "calmlens-typography-heading2",
  heading3: "calmlens-typography-heading3",
  heading4: "calmlens-typography-heading4",
  body: "calmlens-typography-body",
  bodySmall: "calmlens-typography-bodySmall",
  bodyExtraSmall: "calmlens-typography-bodyExtraSmall",
  caption: "calmlens-typography-caption",
  code: "calmlens-typography-code",
  regular: "calmlens-typography-regular",
  medium: "calmlens-typography-medium",
  semibold: "calmlens-typography-semibold",
  bold: "calmlens-typography-bold",
  foreground: "calmlens-typography-foreground",
  muted: "calmlens-typography-muted",
  primary: "calmlens-typography-primary",
  success: "calmlens-typography-success",
  danger: "calmlens-typography-danger",
  center: "calmlens-typography-center",
  right: "calmlens-typography-right",
  truncate: "calmlens-typography-truncate",
};

export type TypographyVariant =
  | "display"
  | "heading1"
  | "heading2"
  | "heading3"
  | "heading4"
  | "body"
  | "bodySmall"
  | "bodyExtraSmall"
  | "caption"
  | "code";

export type TypographyTone = "inherit" | "foreground" | "muted" | "primary" | "success" | "danger";

export type TypographyWeight = "regular" | "medium" | "semibold" | "bold";

type TypographyOwnProps<C extends React.ElementType> = {
  as?: C;
  align?: "left" | "center" | "right";
  tone?: TypographyTone;
  truncate?: boolean;
  variant?: TypographyVariant;
  weight?: TypographyWeight;
};

export type TypographyProps<C extends React.ElementType = "p"> = TypographyOwnProps<C> &
  Omit<React.ComponentPropsWithRef<C>, keyof TypographyOwnProps<C>>;

const defaultElements: Record<TypographyVariant, React.ElementType> = {
  display: "h1",
  heading1: "h1",
  heading2: "h2",
  heading3: "h3",
  heading4: "h4",
  body: "p",
  bodySmall: "p",
  bodyExtraSmall: "p",
  caption: "span",
  code: "code",
};

const Typography = <C extends React.ElementType = "p">(props: TypographyProps<C>) => {
  const {
    align = "left",
    as,
    className,
    tone = "inherit",
    truncate = false,
    variant = "body",
    weight,
    ...otherProps
  } = props;
  const Tag = as ?? defaultElements[variant];

  return (
    <Tag
      className={clsx(
        styles.root,
        styles[variant],
        tone !== "inherit" && styles[tone],
        align !== "left" && styles[align],
        weight && styles[weight],
        truncate && styles.truncate,
        className,
      )}
      data-typography={variant}
      {...otherProps}
    />
  );
};

export default Typography;
